// dashboard/frontend/src/components/DoNotEmail/DoNotEmailList.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpIcon from '@mui/icons-material/Help';
import api from '../../api/axios';

const DoNotEmailList = () => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [newEntry, setNewEntry] = useState({
        value: '',
        reason: ''
    });

    useEffect(() => {
        fetchEntries();
    }, []);

    const fetchEntries = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get('/do-not-email/');
            setEntries(response.data.results || []);
        } catch (error) {
            console.error('Error fetching do-not-email entries:', error);
            setError('Failed to load entries. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        try {
            if (!newEntry.value) {
                setError('Email address or domain is required');
                return;
            }

            // Validate email or domain format
            if (!newEntry.value.includes('@')) {
                setError('Please enter a valid email address or domain (starting with @)');
                return;
            }

            await api.post('/do-not-email/', newEntry);
            setOpenDialog(false);
            setNewEntry({
                value: '',
                reason: ''
            });
            fetchEntries();
        } catch (error) {
            console.error('Error adding entry:', error);
            setError('Failed to add entry. Please try again.');
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/do-not-email/${id}/`);
            fetchEntries();
        } catch (error) {
            console.error('Error deleting entry:', error);
            setError('Failed to delete entry. Please try again.');
        }
    };

    if (loading && entries.length === 0) {
        return (
            <Box display="flex" justifyContent="center" m={3}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                    Do Not Email List
                    <Tooltip title="Enter a full email address to block a specific address, or enter @domain.com to block an entire domain">
                        <IconButton size="small" sx={{ ml: 1 }}>
                            <HelpIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => setOpenDialog(true)}
                >
                    Add Entry
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Email/Domain</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Added</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {entries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No entries found
                                </TableCell>
                            </TableRow>
                        ) : (
                            entries.map((entry) => (
                                <TableRow key={entry.id}>
                                    <TableCell>{entry.value}</TableCell>
                                    <TableCell>{entry.reason}</TableCell>
                                    <TableCell>
                                        {new Date(entry.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() => handleDelete(entry.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog 
                open={openDialog} 
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <Box p={3}>
                    <Typography variant="h6" gutterBottom>
                        Add Do Not Email Entry
                    </Typography>

                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Enter a full email address to block a specific address (e.g., person@domain.com),
                        or enter @domain.com to block an entire domain.
                    </Typography>

                    <TextField
                        fullWidth
                        label="Email Address or Domain"
                        value={newEntry.value}
                        onChange={(e) => setNewEntry({
                            ...newEntry,
                            value: e.target.value.toLowerCase()
                        })}
                        placeholder="person@domain.com or @domain.com"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Reason (Optional)"
                        value={newEntry.reason}
                        onChange={(e) => setNewEntry({
                            ...newEntry,
                            reason: e.target.value
                        })}
                        sx={{ mb: 2 }}
                    />

                    <Box display="flex" gap={2}>
                        <Button
                            variant="contained"
                            onClick={handleAdd}
                            fullWidth
                        >
                            Add Entry
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setOpenDialog(false)}
                            fullWidth
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default DoNotEmailList;